import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/pro-light-svg-icons/faEllipsis';
import { faDownload } from '@fortawesome/pro-light-svg-icons/faDownload';
import { faUserPlus } from '@fortawesome/pro-light-svg-icons/faUserPlus';
import { TypedMemo } from '../../../common/types';
import { LockChip } from '../../components/LockedControl/LockedControl';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useContactActions } from '../hooks/useContactActions';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { downloadCsv } from './downloadCsv';
import { addContactMessages } from './addContactMessages';
import { contactsMessages } from './contactsMessages';

const messages = defineMessages({
  more: { id: 'app.outreach.contacts.more', defaultMessage: 'More' },
});

interface ContactsMoreMenuProps {
  accountId: number;
  listUuid: string | null;
  onAddContact: () => void;
}

/**
 * The "More" menu of the Contacts head (design navigation-v2 d 4d): the head shows only Import and
 * "Pitch a song", so "Add contact" and "Export CSV" sit here. Export is Pro and up: below Pro the
 * row shows the lock chip and a click opens the plan prompt. The file holds only revealed or
 * imported addresses.
 */
const ContactsMoreMenuComponent: React.FC<ContactsMoreMenuProps> = ({ accountId, listUuid, onAddContact }) => {
  const intl = useIntl();
  const plan = useOutreachPlan();
  const { exportCsv, isExporting } = useContactActions(accountId);
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen((open) => !open), []);

  const runExport = useCallback(() => {
    plan.guard(SMLUpgradeFeature.outreachExport, () => {
      exportCsv(listUuid)
        .then((result) => downloadCsv((result as { csv: string }).csv, 'music24-contacts.csv'))
        .catch(console.error);
    });
  }, [exportCsv, listUuid, plan]);

  return (
    <Dropdown isOpen={isOpen} toggle={toggle} direction="down">
      <DropdownToggle color="primary" outline size="sm" className="outline" data-testid="contacts-more">
        <FontAwesomeIcon icon={faEllipsis} className="me-2" />
        <span>{intl.formatMessage(messages.more)}</span>
      </DropdownToggle>
      <DropdownMenu end>
        <DropdownItem onClick={onAddContact} disabled={plan.isDemo}>
          <FontAwesomeIcon icon={faUserPlus} className="me-2" />
          {intl.formatMessage(addContactMessages.open)}
        </DropdownItem>
        <DropdownItem onClick={runExport} disabled={plan.isDemo || isExporting}>
          <FontAwesomeIcon icon={faDownload} className="me-2" />
          {intl.formatMessage(contactsMessages.exportCsv)}
          {!plan.canExport && <LockChip feature={SMLUpgradeFeature.outreachExport} className="ms-2" withSpokenHint />}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export const ContactsMoreMenu = TypedMemo(ContactsMoreMenuComponent);
